import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash-es';

type Size = Readonly<{
  width: number;
  height: number;
  clientWidth: number;
  clientHeight: number;
}>;

const DEBOUNCE_MS = 100;

/**
 * Observes element size changes using ResizeObserver.
 * Defers state updates to requestAnimationFrame to avoid ResizeObserver loop warnings.
 *
 * @template T - HTML element type (defaults to HTMLDivElement)
 * @returns Tuple of [size, elementRef] where size is { width, height, clientWidth, clientHeight }
 *
 * @example
 * const [size, ref] = useSizeObserver();
 */
export function useSizeObserver<T extends HTMLElement = HTMLDivElement>() {
  const frameIdRef = useRef<number | null>(null);
  const elementRef = useRef<T>(null);

  const [size, setSize] = useState<Size>({ width: 0, height: 0, clientWidth: 0, clientHeight: 0 });

  const updateSize = useCallback((size: Size) => {
    // Cancel any pending frame before scheduling a new one
    if (frameIdRef.current) {
      cancelAnimationFrame(frameIdRef.current);
    }

    // Defer state update to avoid ResizeObserver loop warnings
    frameIdRef.current = requestAnimationFrame(() => {
      setSize(size);
      frameIdRef.current = null;
    });
  }, []);

  const debouncedUpdateSize = useMemo(() => {
    return debounce(updateSize, DEBOUNCE_MS);
  }, [updateSize]);

  useEffect(() => {
    const hasResizeObserver = typeof ResizeObserver !== 'undefined';

    if (!hasResizeObserver) return;

    const element = elementRef.current;

    if (!element) return;

    // Ignoring compat ResizeObserver seems sufficiently supported.
    // eslint-disable-next-line compat/compat
    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];

      if (!entry) {
        return;
      }

      const width = entry.contentRect.width;
      const clientWidth = entry.target.clientWidth;
      const height = entry.contentRect.height;
      const clientHeight = entry.target.clientHeight;

      debouncedUpdateSize({
        width,
        height,
        clientWidth,
        clientHeight,
      });
    });

    resizeObserver.observe(element);

    return () => {
      debouncedUpdateSize.cancel();

      // Cancel any pending animation frame
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
        frameIdRef.current = null;
      }

      if (element) {
        resizeObserver.disconnect();
      }
    };
  }, [debouncedUpdateSize]);

  return [size, elementRef] as const;
}
